import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useIntl } from 'react-intl';

import { OrganizationAPIKey } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { useCopyToClipboard } from '@/shared/hooks';
import { Icon, Tooltip } from '@/shared/ui';

import { EMPTY_VALUE } from '../config';
import { formatApiKeyTimestamp, isApiKeyExpired } from '../lib';
import styles from '../ui/OrganizationApiKeys/OrganizationApiKeys.module.scss';

import { useResolveApiKeyAuthor } from './useResolveApiKeyAuthor';

type UseGetApiKeysColumnsParams = {
  canDelete: boolean;
  onDelete: (apiKey: OrganizationAPIKey) => void;
};

export const useGetApiKeysColumns = (params: UseGetApiKeysColumnsParams) => {
  const { canDelete, onDelete } = params;

  const { formatMessage, formatDate } = useIntl();
  const resolveAuthor = useResolveApiKeyAuthor();
  const copyToClipboard = useCopyToClipboard();

  const copyTitle = formatMessage({
    id: 'global.copy',
    defaultMessage: 'Copy',
  });

  const createdByHint = formatMessage({
    id: 'companySettings.apiKeys.createdByHint',
    defaultMessage:
      'This is the ID of the user who created the key. If you have any questions, copy it together with the key ID and send them to support.',
  });

  const columnHelper = createColumnHelper<OrganizationAPIKey>();

  const columnsScheme = [
    columnHelper.accessor((row) => row.ID, {
      id: 'id',
      enableSorting: false,
      minSize: 210,
      header: formatMessage({
        id: 'companySettings.apiKeys.col.id',
        defaultMessage: 'Key ID',
      }),
      cell: (cell) => {
        const id = cell.getValue();

        return (
          <span
            className={cn(styles.keyId, styles.copyable)}
            title={copyTitle}
            onClick={() => copyToClipboard(id)}
          >
            {id}
          </span>
        );
      },
    }),
    columnHelper.accessor((row) => row.Created?.By, {
      id: 'createdBy',
      enableSorting: false,
      minSize: 180,
      header: formatMessage({
        id: 'companySettings.apiKeys.col.createdBy',
        defaultMessage: 'Created by',
      }),
      cell: (cell) => {
        const by = cell.getValue();

        if (!by) {
          return <span className={styles.breakWord}>{EMPTY_VALUE}</span>;
        }

        return (
          <Tooltip.Third
            content={createdByHint}
            delayDuration={800}
            side="top"
            align="start"
          >
            <span
              className={cn(styles.breakWord, styles.copyable)}
              onClick={() => copyToClipboard(by)}
            >
              {resolveAuthor(by)}
            </span>
          </Tooltip.Third>
        );
      },
    }),
    columnHelper.accessor((row) => row.Created?.At, {
      id: 'createdAt',
      enableSorting: false,
      minSize: 150,
      header: formatMessage({
        id: 'companySettings.apiKeys.col.createdAt',
        defaultMessage: 'Created at',
      }),
      cell: (cell) => formatApiKeyTimestamp(formatDate, cell.getValue()),
    }),
    columnHelper.accessor((row) => row.Created?.Comment, {
      id: 'comment',
      enableSorting: false,
      minSize: 160,
      header: formatMessage({
        id: 'companySettings.apiKeys.col.comment',
        defaultMessage: 'Comment',
      }),
      cell: (cell) => (
        <span className={styles.breakWord}>
          {cell.getValue() || EMPTY_VALUE}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.ExpiresAt, {
      id: 'expiresAt',
      enableSorting: false,
      minSize: 150,
      header: formatMessage({
        id: 'companySettings.apiKeys.col.expiresAt',
        defaultMessage: 'Expires at',
      }),
      cell: (cell) => formatApiKeyTimestamp(formatDate, cell.getValue()),
    }),
    columnHelper.accessor((row) => isApiKeyExpired(row), {
      id: 'status',
      enableSorting: false,
      minSize: 90,
      header: formatMessage({ id: 'global.status', defaultMessage: 'Status' }),
      cell: (cell) => (
        <span className={cn(cell.getValue() && styles.expired)}>
          {cell.getValue()
            ? formatMessage({
                id: 'companySettings.apiKeys.status.expired',
                defaultMessage: 'Expired',
              })
            : formatMessage({
                id: 'companySettings.apiKeys.status.active',
                defaultMessage: 'Active',
              })}
        </span>
      ),
    }),
    ...(canDelete
      ? [
          columnHelper.display({
            id: 'actions',
            enableSorting: false,
            size: 48,
            minSize: 48,
            header: '',
            cell: (cell) => (
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => onDelete(cell.row.original)}
              >
                <Icon name="delete" size={32} />
              </button>
            ),
          }),
        ]
      : []),
  ] as ColumnDef<OrganizationAPIKey>[];

  return columnsScheme;
};
